import {ITabBarHeaderItem} from 'design-system/src/Components/Navigation/TabBarHeader/TabBarHeader';
import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {
  ILinkIconFragmentFragment,
  ILinkPicker,
  ILinkPickerFragmentFragment,
  ISectionPageTopMenuFragmentFragment,
} from '~Api/Graphql/gql/types.generated';
import {getLinkPickerUrl} from '~Umbraco/Utils/element/getLinkPickerUrl';
import {mapTopMenuLink} from '~Umbraco/Utils/mapTopMenuLink';
import {RouteFactory} from '~Umbraco/Utils/RouteFactory';
import {TabBarHeaderProps} from 'design-system/src/Components/Navigation/TabBarHeader/TabBarHeader';
import useAppNavigation from '@src/utils/useAppNavigation';
import {Dispatch, AnyAction} from '@reduxjs/toolkit';

type HeaderSubMenu = TabBarHeaderProps;
type MenuItem =
  | {
      content: {__typename: 'LinkPicker'} & ILinkPickerFragmentFragment &
        Pick<ILinkPicker, 'title' | 'subtitle'>;
    }
  | {
      content: {__typename: 'LinkIcon'} & Pick<
        ILinkIconFragmentFragment,
        'link'
      > &
        Pick<ILinkPicker, 'title' | 'subtitle'>;
    }
  | {
      content: {
        __typename: 'ExternalLink';
        title?: string;
        externalUrl?: string;
      };
    }
  | {content: {__typename: 'ExpandMenu'; title?: string; links?: any[]}};

const handleLinkPicker = (
  menu: MenuItem,
  items: any,
  navigation: any,
  dispatch: Dispatch<AnyAction> | undefined,
  output: NonNullable<HeaderSubMenu>['items'],
) => {
  if (menu.content.__typename !== 'LinkPicker') return;
  const {title = ''} = menu.content;
  const {url, __typename, id} = getLinkPickerUrl(menu.content);

  console.log('[useGetTopMenu] handleLinkPicker', menu.content);

  if (!url) return;

  const route = new RouteFactory(
    {url, __typename, title, items, id},
    navigation,
    dispatch,
  );

  output.push({
    title,
    onClick: async () => await route.navigate(),
  });
};

const handleExternalLink = (
  menu: MenuItem,
  items: any,
  navigation: any,
  dispatch: Dispatch<AnyAction> | undefined,
  output: NonNullable<HeaderSubMenu>['items'],
) => {
  if (menu.content.__typename !== 'ExternalLink') return;
  console.log('[useGetTopMenu] handleExternalLink', menu.content);

  const {title = '', externalUrl: url, __typename} = menu.content;

  if (!url) return;

  const route = new RouteFactory(
    {url: url || '', __typename, title, items, id: ''},
    navigation,
    dispatch,
  );

  output.push({
    title,
    onClick: async () => await route.navigate(),
  });
};

const handleExpandMenu = (
  menu: MenuItem,
  items: any,
  navigation: any,
  dispatch: Dispatch<AnyAction> | undefined,
  output: NonNullable<HeaderSubMenu>['items'],
) => {
  if (menu.content.__typename !== 'ExpandMenu') return;
  console.log('[useGetTopMenu] handleExpandMenu', menu.content);

  let children: ITabBarHeaderItem[] = [];
  menu.content.links?.forEach(link => {
    if (link && link.content.__typename === 'LinkPicker') {
      const {title = ''} = link.content;
      const {url, id, __typename} = getLinkPickerUrl(link.content);

      if (!url) return;

      const route = new RouteFactory(
        {url, __typename, id, title, items},
        navigation,
        dispatch,
      );

      children.push({
        title,
        onClick: async () => await route.navigate(),
      });
    }
  });
  
  output.push({
    title: menu.content.title || '',
    children: children,
  });
};

const handlers = {
  LinkPicker: handleLinkPicker,
  ExternalLink: handleExternalLink,
  ExpandMenu: handleExpandMenu,
};

const useGetTopMenu = () => {
  const navigation = useAppNavigation();
  const dispatch = useDispatch();

  const getTopMenu = useCallback(
    (
      topMenu: ISectionPageTopMenuFragmentFragment['topMenu'],
    ): NonNullable<HeaderSubMenu>['items'] | undefined => {
      let items = mapTopMenuLink(topMenu);
      let _output: NonNullable<HeaderSubMenu>['items'] = [];

      const filteredMenus = topMenu?.filter(
        menu =>
          menu &&
          menu.content &&
          ['LinkPicker', 'LinkIcon', 'ExternalLink', 'ExpandMenu'].includes(
            menu.content.__typename,
          ),
      ) as MenuItem[];

      filteredMenus?.forEach(menu => {
        if (
          !menu ||
          !menu.content ||
          typeof menu.content.__typename !== 'string'
        )
          return;
        const handler =
          handlers[menu.content.__typename as keyof typeof handlers];
        if (handler) handler(menu, items, navigation, dispatch, _output);
      });
      return _output;
    },
    [dispatch, navigation],
  );

  return {getTopMenu};
};

export default useGetTopMenu;
